import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Eye, Calendar, Wrench, User as UserIcon, Building2, MapPin, Clock, AlertTriangle, ShieldCheck, Sparkles, PoundSterling, Bookmark, BookmarkCheck, CheckCircle2, Undo2, Pencil, X, FileDown, PenTool } from 'lucide-react';
import { uploadWorkOrderAttachment, approveWorkOrder, rejectWorkOrder, resolvePendingLabourRate, getInventoryParts, updateWorkOrder, getAssets, getUsers, fetchWorkOrderPdfUrl } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { AttachmentUploader } from './Attachments';
import { StatusBadge, PriorityBadge } from './Shared';
import { WorkOrderPartsPanel } from './WorkOrderPartsPanel';
import { WorkOrderLaborPanel } from './WorkOrderLaborPanel';
import { WorkOrderRamsPanel } from './WorkOrderRamsPanel';
import { SignOffModal } from './SignaturePad';
import { PdfPreflightModal } from './PdfPreflightModal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { pinWorkOrder, unpinWorkOrder, isWorkOrderPinned } from '../lib/offlinePin';
import { usePdfPreview } from '../hooks/usePdfPreview';
import { PdfPreviewDialog } from './PdfPreviewDialog';

/**
 * Turns an axios/fetch error from `updateWorkOrder` (and friends) into
 * a human-readable one-liner for the red save toast. Precedence:
 *   1. Server-supplied `detail` on the JSON body — the usual FastAPI
 *      shape. `detail` can be a string OR (on 422 validation errors)
 *      an array of `{loc, msg, type}` — join those into a compact
 *      "field: reason" list so the admin actually sees which field
 *      the API rejected.
 *   2. Alternate JSON fields (`message`, `error`) some middlewares use.
 *   3. Raw text body when the response wasn't JSON (e.g. 502 from the
 *      ingress) — trimmed to a sane length.
 *   4. `${status} ${statusText}` when the response had no body.
 *   5. The JS `Error.message` when the request never left the browser
 *      (network drop, CORS block, etc.).
 *
 * Prefixed with the given `label` — e.g. "Could not save" — so both
 * red toasts on the WO edit form read the same way:
 *   `Could not save — 409: Customer is not linked to a Xero contact`.
 */
const saveErrorMessage = (err, label = 'Could not save') => {
  const resp = err?.response;
  const status = resp?.status;
  const body = resp?.data;
  let reason = '';
  if (body && typeof body === 'object') {
    if (typeof body.detail === 'string') reason = body.detail;
    else if (Array.isArray(body.detail)) {
      reason = body.detail
        .map((d) => {
          const loc = Array.isArray(d?.loc) ? d.loc.filter((x) => x !== 'body').join('.') : '';
          const msg = d?.msg || '';
          return loc ? `${loc}: ${msg}` : msg;
        })
        .filter(Boolean)
        .join('; ');
    } else if (typeof body.message === 'string') reason = body.message;
    else if (typeof body.error === 'string') reason = body.error;
  } else if (typeof body === 'string' && body.trim()) {
    reason = body.trim().slice(0, 300);
  }
  if (!reason && status) reason = `${status}${resp?.statusText ? ` ${resp.statusText}` : ''}`;
  if (!reason && err?.message) reason = err.message;
  if (!reason) return label;
  return status ? `${label} — ${status}: ${reason}` : `${label} — ${reason}`;
};

/**
 * Computes the internal cost of a WO from its logged labour + parts.
 *   labour:  sum(`lineTotal` || hours * rate)
 *   parts:   sum(qty * unitCost)        (note: cost, NOT sell price)
 * Used by both the read-only chip and the inline edit form so the admin
 * can sanity-check a fixed-price quote against actual job cost before
 * invoicing.
 */
const computeWoInternalCost = (wo) => {
  const labour = (wo.labor || []).reduce((a, l) => {
    const lineTotal = Number(l.lineTotal);
    if (Number.isFinite(lineTotal) && lineTotal > 0) return a + lineTotal;
    return a + (Number(l.hours) || 0) * (Number(l.rate) || 0);
  }, 0);
  const parts = (wo.partsUsed || []).reduce(
    (a, p) => a + (Number(p.qty) || 0) * (Number(p.unitCost) || 0),
    0,
  );
  return labour + parts;
};

/**
 * MarginBadge — compact "Margin: 32% · £270" pill that compares an
 * agreed fixed price to the WO's accumulated labour + parts cost. Tone
 * coded so the admin spots margin trouble at a glance:
 *   ≥ 30% → emerald (healthy)
 *   10-30% → amber (tight)
 *   < 10% / negative → rose (loss)
 *
 * Returns null when no cost has been logged yet (margin is meaningless
 * before the engineer starts the job) or when the agreed price is 0.
 */

/**
 * Renders the WO's linked assets. Supports the new multi-asset model
 * (`wo.assetIds`) with a graceful fallback to legacy single-asset WOs
 * (`wo.asset` string). Uses `getAssets` lazily so we can show the
 * asset id and location on hover — the WO document only stores the
 * primary asset's name.
 */
const AssetsDisplay = ({ wo }) => {
  const [namesById, setNamesById] = useState({});
  const ids = Array.isArray(wo.assetIds) && wo.assetIds.length
    ? wo.assetIds
    : (wo.assetId ? [wo.assetId] : []);
  useEffect(() => {
    if (ids.length <= 1) return; // primary name already on wo.asset
    let alive = true;
    getAssets().then(list => {
      if (!alive) return;
      const map = {};
      (list || []).forEach(a => { map[a.id] = a.name; });
      setNamesById(map);
    }).catch(() => {});
    return () => { alive = false; };
  }, [ids.join(',')]);
  if (ids.length === 0) return <span>—</span>;
  if (ids.length === 1) return <span>{wo.asset || namesById[ids[0]] || ids[0]}</span>;
  return (
    <div className="flex flex-wrap gap-1" data-testid={`wo-assets-list-${wo.id}`}>
      {ids.map((id, idx) => (
        <span
          key={id}
          className="inline-flex items-center bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-xs"
          title={id}
        >
          {(idx === 0 ? wo.asset : namesById[id]) || id}
        </span>
      ))}
    </div>
  );
};

/**
 * Completion-date pill for a closed WO. Two modes:
 *  · admin → click to reveal an inline `<input type="date">` +
 *    save/cancel controls. Saves via a PATCH just to `completedAt`.
 *    Debounced only by the network round-trip.
 *  · non-admin → static text pill.
 * Kept as a purpose-built component (rather than a generic
 * inline-editor) because the completion date drives payroll buckets +
 * the monthly margin chart, so we want a clear per-field affordance
 * and a confirmation toast whenever an admin changes it.
 */
const CompletedAtPill = ({ wo, canEdit, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(wo.completedAt || '');
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { setValue(wo.completedAt || ''); }, [wo.completedAt]);
  const save = async () => {
    if (!value) return toast.error('Pick a date');
    if (value === (wo.completedAt || '')) { setEditing(false); return; }
    setBusy(true);
    try {
      await updateWorkOrder(wo.id, { completedAt: value });
      toast.success('Completion date updated');
      setEditing(false);
      onUpdate?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update date');
    } finally {
      setBusy(false);
    }
  };
  if (editing) {
    return (
      <div className="flex items-center gap-1.5 flex-wrap" data-testid={`wo-completed-at-editor-${wo.id}`}>
        <Calendar className="h-4 w-4 text-emerald-600 shrink-0" />
        <input
          type="date"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="border border-slate-300 rounded px-2 py-0.5 text-sm"
          data-testid={`wo-completed-at-input-${wo.id}`}
        />
        <Button
          size="sm"
          className="h-7 px-2 bg-emerald-700 hover:bg-emerald-800 text-white text-xs"
          onClick={save}
          disabled={busy}
          data-testid={`wo-completed-at-save-${wo.id}`}
        >
          {busy ? 'Saving…' : 'Save'}
        </Button>
        <button
          type="button"
          className="text-xs text-slate-500 hover:text-slate-800 px-1"
          onClick={() => { setValue(wo.completedAt || ''); setEditing(false); }}
          disabled={busy}
        >
          Cancel
        </button>
      </div>
    );
  }
  return (
    <div
      className={`flex items-center gap-2 text-slate-700 ${canEdit ? 'cursor-pointer hover:text-slate-900' : ''}`}
      onClick={canEdit ? () => setEditing(true) : undefined}
      data-testid={`wo-completed-at-${wo.id}`}
      title={canEdit ? 'Click to edit completion date' : ''}
    >
      <Calendar className="h-4 w-4 text-emerald-600" />
      <span>
        <span className="text-xs uppercase tracking-wider text-slate-500 mr-1">Completed</span>
        {wo.completedAt || '—'}
        {canEdit && <Pencil className="h-3 w-3 text-slate-400 inline-block ml-1.5 -mt-0.5" />}
      </span>
    </div>
  );
};


const MarginBadge = ({ price, cost, testId }) => {
  if (!Number.isFinite(price) || price <= 0) return null;
  if (!Number.isFinite(cost) || cost <= 0) return null;
  const profit = price - cost;
  const pct = (profit / price) * 100;
  let tone = 'bg-emerald-50 text-emerald-800 border-emerald-200';
  if (pct < 10) tone = 'bg-rose-50 text-rose-800 border-rose-200';
  else if (pct < 30) tone = 'bg-amber-50 text-amber-800 border-amber-200';
  const profitLabel = `${profit >= 0 ? '+' : '−'}£${Math.abs(profit).toFixed(2)}`;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] tabular-nums ${tone}`}
      title={`Agreed £${price.toFixed(2)} − cost £${cost.toFixed(2)} = ${profitLabel}`}
      data-testid={testId}
    >
      <span className="font-semibold">Margin {pct.toFixed(0)}%</span>
      <span className="opacity-60">·</span>
      <span>{profitLabel}</span>
    </span>
  );
};

/**
 * BillingPanel — admin-visible chip + inline edit for the WO's billing
 * model. `hourly` rolls labour + parts into the customer's draft
 * invoice on close (default). `fixed` ignores logged hours / parts and
 * bills the agreed amount as a single line. The engineer never sees
 * this — they always log their hours so we have the data for our own
 * records — but admins can flip the toggle and tweak the price up to
 * the moment the job is closed.
 *
 * Once an invoice has been raised the WO becomes read-only here too:
 * the line item is already on the customer's invoice and changing the
 * billing model won't retroactively fix anything, so we hide the edit.
 */
const BillingPanel = ({ wo, onUpdate }) => {
  const isFixed = (wo.billingType || 'hourly') === 'fixed';
  const isLocked = wo.status === 'completed' || !!wo.invoiceId;
  const [editing, setEditing] = useState(false);
  const [type, setType] = useState(isFixed ? 'fixed' : 'hourly');
  const [amount, setAmount] = useState(
    wo.fixedPriceAmount != null ? String(wo.fixedPriceAmount) : ''
  );
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    setType(isFixed ? 'fixed' : 'hourly');
    setAmount(wo.fixedPriceAmount != null ? String(wo.fixedPriceAmount) : '');
  }, [wo.id, wo.billingType, wo.fixedPriceAmount, isFixed]);

  const save = async () => {
    if (type === 'fixed') {
      const amt = Number(amount);
      if (!Number.isFinite(amt) || amt <= 0) return toast.error('Enter the agreed price.');
    }
    setSaving(true);
    try {
      const payload = type === 'fixed'
        ? { billingType: 'fixed', fixedPriceAmount: Number(amount) }
        : { billingType: 'hourly', fixedPriceAmount: 0 };
      await updateWorkOrder(wo.id, payload);
      toast.success('Billing updated');
      setEditing(false);
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error(saveErrorMessage(e, 'Update failed'));
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    const woCost = computeWoInternalCost(wo);
    return (
      <div
        className="mt-2 inline-flex items-center gap-2 flex-wrap"
        data-testid={`wo-billing-${wo.id}`}
      >
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs ${
            isFixed
              ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
              : 'bg-slate-100 text-slate-700 border-slate-200'
          }`}
        >
          <PoundSterling className="h-3 w-3" />
          {isFixed
            ? `Fixed price · £${Number(wo.fixedPriceAmount || 0).toFixed(2)}`
            : 'Hourly + materials'}
        </span>
        {isFixed && (
          <MarginBadge
            price={Number(wo.fixedPriceAmount || 0)}
            cost={woCost}
            testId={`wo-billing-margin-${wo.id}`}
          />
        )}
        {!isLocked && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-[11px] text-slate-500 hover:text-slate-800 underline underline-offset-2"
            data-testid={`wo-billing-edit-${wo.id}`}
          >
            Change
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2"
      data-testid={`wo-billing-edit-form-${wo.id}`}
    >
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => setType('hourly')}
          className={`rounded-md border px-2.5 py-1.5 text-xs text-left ${
            type === 'hourly' ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white'
          }`}
          data-testid={`wo-billing-edit-hourly-${wo.id}`}
        >
          Hourly + materials
        </button>
        <button
          type="button"
          onClick={() => setType('fixed')}
          className={`rounded-md border px-2.5 py-1.5 text-xs text-left ${
            type === 'fixed' ? 'border-emerald-700 bg-emerald-700 text-white' : 'border-slate-200 bg-white'
          }`}
          data-testid={`wo-billing-edit-fixed-${wo.id}`}
        >
          Fixed price
        </button>
      </div>
      {type === 'fixed' && (
        <div className="space-y-1.5">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm pointer-events-none">£</span>
            <Input
              type="number"
              step="0.01"
              min="0"
              inputMode="decimal"
              placeholder="e.g. 850.00"
              className="pl-7 h-9"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid={`wo-billing-edit-amount-${wo.id}`}
            />
          </div>
          {(() => {
            const cost = computeWoInternalCost(wo);
            if (cost <= 0) {
              return (
                <p className="text-[11px] text-slate-500">
                  No labour or parts logged yet — margin will appear here once the engineer logs work.
                </p>
              );
            }
            return (
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] text-slate-500">
                  Logged cost so far: <span className="font-semibold text-slate-700">£{cost.toFixed(2)}</span>
                </span>
                <MarginBadge
                  price={Number(amount)}
                  cost={cost}
                  testId={`wo-billing-edit-margin-${wo.id}`}
                />
              </div>
            );
          })()}
        </div>
      )}
      <div className="flex items-center gap-2 justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setEditing(false)}
          disabled={saving}
          data-testid={`wo-billing-edit-cancel-${wo.id}`}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={save}
          disabled={saving}
          className="bg-slate-900 hover:bg-slate-800 text-white"
          data-testid={`wo-billing-edit-save-${wo.id}`}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

/**
 * LabourRollup — small badge under the WO title summing all labour entries.
 *
 *   "4.20h logged · £147.00"
 *
 * If the WO has an `estimatedHours` set AND logged hours are within 80% of
 * it, an amber warning prefix appears so the admin spots scope-creep
 * before invoicing. Over-100% goes red. Hidden entirely when no labour
 * is logged yet (the empty case is already covered by the panel below).
 */
const LabourRollup = ({ wo, isEngineer }) => {
  const labor = wo.labor || [];
  if (labor.length === 0) return null;
  const totalHours = labor.reduce((a, l) => a + (Number(l.hours) || 0), 0);
  const totalCost = labor.reduce((a, l) => a + (Number(l.lineTotal) || 0), 0);
  // Prefer `totalHoursEstimate` — the admin-set target on the SLA
  // panel — over the legacy `estimatedHours` field which defaults to 4
  // on every WO and is virtually never edited. Using the wrong field
  // caused "1400% over estimate" on WOs whose real target was 60h.
  const estimate =
    Number(wo.totalHoursEstimate) > 0
      ? Number(wo.totalHoursEstimate)
      : Number(wo.estimatedHours) || 0;
  // New variance rule (Feb 2026 fix — Max reported that logging
  // exactly the estimated hours rendered "0% over estimate" in red):
  //   variance = ((logged - estimate) / estimate) × 100
  //     under 0   → green  "N% under estimate"
  //     exactly 0 → neutral "On estimate"  (never red)
  //     over 0    → red    "N% over estimate"
  //   estimate ≤ 0 → no chip variant, hide the warning altogether.
  // Rounded before branching so ±0.5% float noise on 1h/1h reads as
  // "On estimate" rather than flipping to red at 0.0001% over.
  let tone = 'bg-slate-100 text-slate-700 border-slate-200';
  let warn = null;
  if (estimate > 0) {
    const variance = ((totalHours - estimate) / estimate) * 100;
    const rounded = Math.round(variance);
    if (rounded === 0) {
      warn = (
        <span className="ml-1.5 text-slate-600" data-testid="wo-labour-on-estimate">
          On estimate
        </span>
      );
    } else if (rounded < 0) {
      tone = 'bg-emerald-50 text-emerald-800 border-emerald-200';
      warn = (
        <span
          className="ml-1.5 text-emerald-700"
          data-testid="wo-labour-under-estimate"
        >
          {Math.abs(rounded)}% under estimate
        </span>
      );
    } else {
      tone = 'bg-rose-50 text-rose-800 border-rose-200';
      warn = (
        <span
          className="inline-flex items-center gap-1 ml-1.5 text-rose-700"
          data-testid="wo-labour-over-estimate"
        >
          <AlertTriangle className="h-3 w-3" />
          {rounded}% over estimate
        </span>
      );
    }
  }
  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 mt-2 rounded-full border text-xs ${tone}`}
      data-testid="wo-labour-rollup"
    >
      <Clock className="h-3 w-3" />
      <span className="tabular-nums font-medium">{totalHours.toFixed(2)}h logged</span>
      {!isEngineer && (
        <>
          <span className="opacity-50">·</span>
          <span className="tabular-nums font-semibold">£{totalCost.toFixed(2)}</span>
        </>
      )}
      {warn}
    </div>
  );
};

/**
 * Admin-only inline edit form for a WO's core metadata: title,
 * description, priority, due date, assignee, asset, customer, and the
 * SLA fields. Status / billing model / parts / labour / RAMS each have
 * their own dedicated controls elsewhere in the dialog and are NOT
 * shown here — splitting them keeps each editor focused on one concern.
 *
 * Fetches assets + users lazily on first open so we don't hammer the
 * API when the dialog opens for read-only viewing.
 *
 * The save payload is a `PATCH /work-orders/{id}` with only the changed
 * fields; the backend already enforces that engineers can't edit SLA
 * / billing fields, but we hide this whole form from non-admins anyway.
 */
const EditWorkOrderForm = ({ wo, onClose, onSaved }) => {
  const [form, setForm] = useState({
    title: wo.title || '',
    description: wo.description || '',
    priority: wo.priority || 'medium',
    dueDate: wo.dueDate || '',
    // Multi-assignee. Falls back to the legacy singular `assigneeId`
    // for WOs that pre-date this migration.
    assigneeIds: Array.isArray(wo.assigneeIds) && wo.assigneeIds.length
      ? [...wo.assigneeIds]
      : (wo.assigneeId ? [wo.assigneeId] : []),
    // Multi-asset. Falls back to the legacy single `assetId` so WOs
    // created before this migration still round-trip cleanly.
    assetIds: Array.isArray(wo.assetIds) && wo.assetIds.length
      ? [...wo.assetIds]
      : (wo.assetId ? [wo.assetId] : []),
    customerId: wo.customerId || '',
    siteCustomerId: wo.siteCustomerId || '',
    category: wo.category || 'Mechanical',
    startByDate: wo.startByDate || '',
    finishByDate: wo.finishByDate || '',
    totalHoursEstimate: wo.totalHoursEstimate ? String(wo.totalHoursEstimate) : '',
  });
  const [assets, setAssets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([getAssets().catch(() => []), getUsers().catch(() => [])])
      .then(([a, u]) => {
        if (!alive) return;
        setAssets(a || []);
        setUsers(u || []);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const employees = users.filter(u => u.role === 'employee' || u.role === 'subcontractor');
  const customers = users.filter(u => u.role === 'customer');
  // Filter assets to the picked customer so admin doesn't have to wade
  // through the entire fleet (matches the create-WO form's behaviour).
  const filteredAssets = form.customerId
    ? assets.filter(a => a.customerId === form.customerId)
    : assets;

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      // Only send fields that actually changed — keeps PATCH payloads
      // small and avoids overwriting fields with stale form state.
      const orig = {
        title: wo.title || '',
        description: wo.description || '',
        priority: wo.priority || 'medium',
        dueDate: wo.dueDate || '',
        assigneeIds: Array.isArray(wo.assigneeIds) && wo.assigneeIds.length
          ? [...wo.assigneeIds]
          : (wo.assigneeId ? [wo.assigneeId] : []),
        assetIds: Array.isArray(wo.assetIds) && wo.assetIds.length
          ? [...wo.assetIds]
          : (wo.assetId ? [wo.assetId] : []),
        customerId: wo.customerId || '',
        siteCustomerId: wo.siteCustomerId || '',
        category: wo.category || 'Mechanical',
        startByDate: wo.startByDate || '',
        finishByDate: wo.finishByDate || '',
        totalHoursEstimate: wo.totalHoursEstimate ? String(wo.totalHoursEstimate) : '',
      };
      const payload = {};
      for (const k of Object.keys(form)) {
        // Arrays need value-equal comparison, not reference-equal.
        if (k === 'assetIds' || k === 'assigneeIds') {
          if (JSON.stringify(form[k]) !== JSON.stringify(orig[k])) payload[k] = form[k];
        } else if (form[k] !== orig[k]) {
          payload[k] = form[k];
        }
      }
      // Coerce hours estimate back to a number (or 0 if cleared).
      if ('totalHoursEstimate' in payload) {
        payload.totalHoursEstimate = Number(payload.totalHoursEstimate) || 0;
      }
      if (Object.keys(payload).length === 0) {
        toast.message('Nothing to save');
        onClose?.();
        return;
      }
      await updateWorkOrder(wo.id, payload);
      toast.success('Work order updated');
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(saveErrorMessage(e, 'Could not save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded-md border border-blue-200 bg-blue-50/40 p-4 space-y-3"
      data-testid={`wo-edit-form-${wo.id}`}
    >
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-blue-900 font-semibold">
          Edit work order
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-slate-500 hover:text-slate-800"
          data-testid={`wo-edit-close-${wo.id}`}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* When the WO is already on an invoice, edits to title /
          description / SLA dates / etc. don't auto-flow to the existing
          invoice line. Make that explicit so admins don't expect the
          customer to see the new wording until we re-push. */}
      {wo.invoiceId && (
        <div
          className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          data-testid={`wo-edit-invoiced-warning-${wo.id}`}
        >
          This work order sits on invoice {wo.invoiceXeroNumber || wo.invoiceId}.
          Editing here updates the WO record only — the existing invoice line
          stays as-is unless you regenerate or re-push the invoice.
        </div>
      )}

      <div>
        <label className="text-xs text-slate-600">Title</label>
        <Input
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="mt-1 bg-white"
          placeholder="e.g. Replace conveyor motor"
          data-testid={`wo-edit-title-${wo.id}`}
        />
      </div>

      <div>
        <label className="text-xs text-slate-600">Description</label>
        <Textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          rows={3}
          className="mt-1 bg-white resize-none"
          data-testid={`wo-edit-description-${wo.id}`}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-slate-600">Priority</label>
          <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
            <SelectTrigger className="mt-1 bg-white" data-testid={`wo-edit-priority-${wo.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-slate-600">Due date</label>
          <Input
            type="date"
            value={form.dueDate}
            onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
            className="mt-1 bg-white"
            data-testid={`wo-edit-due-${wo.id}`}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-slate-600">Customer (billed to)</label>
          <Select
            value={form.customerId || 'internal'}
            onValueChange={(v) => setForm({
              ...form,
              customerId: v === 'internal' ? '' : v,
              // When customer changes, drop assets that don't belong to
              // the newly-picked customer.
              assetIds: form.assetIds.filter(id => {
                const a = assets.find(x => x.id === id);
                return !a || (a.customerId || '') === (v === 'internal' ? '' : v);
              }),
            })}
          >
            <SelectTrigger className="mt-1 bg-white" data-testid={`wo-edit-customer-${wo.id}`}>
              <SelectValue placeholder="Internal" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="internal">Internal</SelectItem>
              {customers.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-slate-600">Site (physical location)</label>
          <Select
            value={form.siteCustomerId || 'same'}
            onValueChange={(v) => setForm({
              ...form,
              siteCustomerId: v === 'same' ? '' : v,
            })}
          >
            <SelectTrigger className="mt-1 bg-white" data-testid={`wo-edit-site-customer-${wo.id}`}>
              <SelectValue placeholder="Same as customer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="same">Same as customer</SelectItem>
              {customers
                .filter(c => c.id !== form.customerId)
                .map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>
                ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500 mt-1">Set this when the work is done at another company's site (e.g. subcontracted job).</p>
        </div>
      </div>

      <div>
        <label className="text-xs text-slate-600">Assets</label>
          <Select
            value=""
            onValueChange={(v) => {
              if (!v || v === 'none') return;
              setForm(f => {
                if (f.assetIds.includes(v)) return f;
                const picked = assets.find(a => a.id === v);
                // Only snap the customer field on the FIRST add (empty
                // customer + empty list). Once anything's set the admin
                // is intentionally curating and we don't overwrite them.
                const nextCustomerId = (!f.customerId && f.assetIds.length === 0 && picked?.customerId)
                  ? picked.customerId
                  : f.customerId;
                return { ...f, assetIds: [...f.assetIds, v], customerId: nextCustomerId };
              });
            }}
          >
            <SelectTrigger className="mt-1 bg-white" data-testid={`wo-edit-asset-${wo.id}`}>
              <SelectValue placeholder={loading ? 'Loading…' : (form.assetIds.length ? 'Add another asset' : 'Select asset(s)')} />
            </SelectTrigger>
            <SelectContent>
              {filteredAssets
                .filter(a => !form.assetIds.includes(a.id))
                .map(a => (
                  <SelectItem key={a.id} value={a.id}>{a.name} ({a.id})</SelectItem>
                ))}
            </SelectContent>
          </Select>
          {form.assetIds.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`wo-edit-asset-chips-${wo.id}`}>
              {form.assetIds.map(id => {
                const a = assets.find(x => x.id === id);
                const label = a ? `${a.name} (${a.id})` : id;
                return (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 bg-slate-100 text-slate-800 border border-slate-200 rounded-full pl-2.5 pr-1 py-0.5 text-xs"
                  >
                    {label}
                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, assetIds: f.assetIds.filter(x => x !== id) }))}
                      className="h-5 w-5 rounded-full hover:bg-slate-200 text-slate-500 hover:text-slate-800 flex items-center justify-center"
                      aria-label={`Remove ${label}`}
                      data-testid={`wo-edit-asset-remove-${id}`}
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>

      <div>
        <label className="text-xs text-slate-600">Assignees</label>
        <Select
          value=""
          onValueChange={(v) => {
            const next = v === 'none' ? '' : v;
            if (!next) return;
            setForm(f => f.assigneeIds.includes(next)
              ? f
              : { ...f, assigneeIds: [...f.assigneeIds, next] });
          }}
        >
          <SelectTrigger className="mt-1 bg-white" data-testid={`wo-edit-assignee-${wo.id}`}>
            <SelectValue placeholder={loading ? 'Loading…' : (form.assigneeIds.length ? 'Add another engineer' : 'Pick engineer(s)')} />
          </SelectTrigger>
          <SelectContent>
            {employees
              .filter(e => !form.assigneeIds.includes(e.id))
              .map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {form.assigneeIds.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`wo-edit-assignee-chips-${wo.id}`}>
            {form.assigneeIds.map(id => {
              const e = employees.find(x => x.id === id);
              const label = e ? e.name : id;
              return (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 bg-white text-slate-800 border border-slate-300 rounded-full pl-2.5 pr-1 py-0.5 text-xs"
                  data-testid={`wo-edit-assignee-chip-${id}`}
                >
                  {label}
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, assigneeIds: f.assigneeIds.filter(x => x !== id) }))}
                    className="h-5 w-5 rounded-full hover:bg-slate-200 text-slate-500 hover:text-slate-800 flex items-center justify-center"
                    aria-label={`Remove ${label}`}
                    data-testid={`wo-edit-assignee-remove-${id}`}
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1 border-t border-blue-100">
        <div>
          <label className="text-xs text-slate-600">Start by</label>
          <Input
            type="date"
            value={form.startByDate}
            onChange={(e) => setForm({ ...form, startByDate: e.target.value })}
            className="mt-1 bg-white"
            data-testid={`wo-edit-startby-${wo.id}`}
          />
        </div>
        <div>
          <label className="text-xs text-slate-600">Finish by</label>
          <Input
            type="date"
            value={form.finishByDate}
            onChange={(e) => setForm({ ...form, finishByDate: e.target.value })}
            className="mt-1 bg-white"
            data-testid={`wo-edit-finishby-${wo.id}`}
          />
        </div>
      </div>

      <div>
        <label className="text-xs text-slate-600">Hours estimate</label>
        <Input
          type="number"
          step="0.25"
          min="0"
          inputMode="decimal"
          placeholder="e.g. 4"
          value={form.totalHoursEstimate}
          onChange={(e) => setForm({ ...form, totalHoursEstimate: e.target.value })}
          className="mt-1 bg-white"
          data-testid={`wo-edit-hours-${wo.id}`}
        />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onClose}
          disabled={saving}
          data-testid={`wo-edit-cancel-${wo.id}`}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={save}
          disabled={saving}
          className="bg-slate-900 hover:bg-slate-800 text-white"
          data-testid={`wo-edit-save-${wo.id}`}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  );
};

/**
 * Controlled detail dialog for a single work order. Parent owns the `open`
 * state so row-level click handlers (tap anywhere on the row / card) can
 * open the dialog without rendering a visible button.
 */
export const WorkOrderDetailDialog = ({ wo, open, onOpenChange, onUpdate, canAttach = true }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isEngineer = user?.role === 'employee' || user?.role === 'subcontractor';
  // PDF preview hook — admin & engineer can export the WO as a Lutrom-
  // branded PDF for handover / customer record. Customers see the same
  // button if the WO belongs to their org.
  const pdf = usePdfPreview();
  const exportPdf = () => {
    if (!wo) return;
    pdf.preview(() => fetchWorkOrderPdfUrl(wo.id));
  };
  // Tracks the offline-pin state for *this* WO. Hydrated on every open so
  // state stays fresh if the user pinned/unpinned from another tab.
  const [pinned, setPinned] = useState(() => (wo ? isWorkOrderPinned(wo.id) : false));
  const [pinBusy, setPinBusy] = useState(false);
  // Sign-off modal — only opens when the admin (any WO) or assignee
  // engineer explicitly requests it. State kept at dialog level so
  // any nested panel can trigger it.
  const [signOffOpen, setSignOffOpen] = useState(false);
  const [preflightOpen, setPreflightOpen] = useState(false);
  // Local sign-off overlay — the parent's `onUpdate` re-fetches the
  // WO list, but the dialog keeps rendering the old `wo` prop until
  // the parent re-passes. Overlaying the response here lets the pill
  // flip instantly without waiting for a full re-open cycle.
  const [signOffOverlay, setSignOffOverlay] = useState(null);
  useEffect(() => { setSignOffOverlay(null); }, [wo?.id]);
  const woView = signOffOverlay ? { ...wo, signOff: signOffOverlay } : wo;
  const canSignOff = isAdmin || (isEngineer && (
    wo?.assigneeId === user?.id
    || (Array.isArray(wo?.assigneeIds) && wo.assigneeIds.includes(user?.id))
  ));
  // Inline edit-mode for the WO's core metadata. Admin-only and hidden
  // when the WO is locked (completed/invoiced) — at that point the doc
  // is effectively a historical record and shouldn't change.
  const [editing, setEditing] = useState(false);
  const [reopenBusy, setReopenBusy] = useState(false);
  React.useEffect(() => {
    // Always close the edit form when the dialog closes or the WO changes
    // out from under us, so the next opener sees a clean state.
    if (!open) setEditing(false);
  }, [open, wo?.id]);

  const reopen = async () => {
    if (!wo) return;
    const hasInvoice = !!wo.invoiceId;
    const msg = hasInvoice
      ? `Reopen ${wo.id}?\n\nThis WO has already been added to draft invoice ${wo.invoiceId}. The line item stays on the invoice — reopening only unlocks the WO so you can edit labour / parts / RAMS. Unlink the line manually from the invoice page if you need it removed.`
      : `Reopen ${wo.id}?\n\nThe WO will return to "In Progress" and the completion date will be cleared. Re-close it whenever the work is actually finished.`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    setReopenBusy(true);
    try {
      await updateWorkOrder(wo.id, {
        status: 'in_progress',
        // Empty string explicitly clears the date — backend keeps the
        // field set to '' rather than re-stamping today, which would be
        // worse than no date at all.
        completedAt: '',
        // Drop the engineer's closure summary so the next close cycle
        // starts clean and the admin-review banner re-fires.
        closureSummary: '',
        adminReviewedAt: '',
        adminReviewedBy: '',
      });
      toast.success('Work order reopened');
      onUpdate?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reopen failed');
    } finally {
      setReopenBusy(false);
    }
  };
  React.useEffect(() => {
    if (open && wo) setPinned(isWorkOrderPinned(wo.id));
  }, [open, wo]);
  if (!wo) return null;
  const togglePin = async () => {
    if (pinBusy) return;
    setPinBusy(true);
    try {
      if (pinned) {
        unpinWorkOrder(wo);
        setPinned(false);
        toast.success('Removed from offline');
      } else {
        setPinned(true);                       // optimistic — rollback if it fails
        const ok = await pinWorkOrder(wo);
        if (ok) {
          toast.success('Saved for offline — including attachments');
        } else {
          toast.warning('Saved partially — some attachments couldn\'t be fetched');
        }
      }
    } catch (_e) {
      setPinned(isWorkOrderPinned(wo.id));
      toast.error('Could not save for offline');
    } finally {
      setPinBusy(false);
    }
  };
  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl w-[calc(100vw-1rem)] sm:w-full max-h-[85vh] overflow-y-auto overflow-x-hidden"
        data-testid={`wo-detail-${wo.id}`}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap pr-10">
            <span className="font-mono text-sm text-slate-500">{wo.id}</span>
            <PriorityBadge priority={wo.priority} />
            <StatusBadge status={wo.status} />
            <button
              type="button"
              onClick={togglePin}
              disabled={pinBusy}
              title={pinned ? 'Saved for offline — tap to remove' : 'Save this job brief for offline'}
              aria-label={pinned ? 'Remove from offline' : 'Save for offline'}
              className={`ml-auto inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-colors disabled:opacity-60 ${
                pinned
                  ? 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100'
                  : 'text-slate-500 border border-slate-200 hover:bg-slate-50 hover:text-slate-700'
              }`}
              data-testid={`wo-offline-pin-${wo.id}`}
            >
              {pinned ? <BookmarkCheck className="h-3.5 w-3.5" /> : <Bookmark className="h-3.5 w-3.5" />}
              <span className="hidden sm:inline">{pinned ? 'Offline' : 'Save offline'}</span>
            </button>
            {/* Export PDF — Lutrom-branded WO summary using the active
                `work_order` template if set, otherwise a hardcoded
                fallback layout. Available to every role that can
                already see this dialog (admin / engineer / owning
                customer). */}
            <button
              type="button"
              onClick={exportPdf}
              disabled={pdf.busy}
              title="Export this work order as a Lutrom PDF"
              aria-label="Export work order PDF"
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-60"
              data-testid={`wo-export-pdf-${wo.id}`}
            >
              <FileDown className={`h-3.5 w-3.5 ${pdf.busy ? 'animate-pulse' : ''}`} />
              <span className="hidden sm:inline">{pdf.busy ? 'Building…' : 'PDF'}</span>
            </button>
            {/* Admin-only "PDF preflight" — read-only sanity check that
                shows what each PDF section will render and flags any
                missing fields (site, sign-off, letterhead, RAMS) so
                the admin can fix them before sending the PDF out. */}
            {isAdmin && (
              <button
                type="button"
                onClick={() => setPreflightOpen(true)}
                title="Preview what the WO PDF will render + flag missing fields"
                aria-label="Preflight WO PDF"
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                data-testid={`wo-preflight-pdf-${wo.id}`}
              >
                <ShieldCheck className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Preflight</span>
              </button>
            )}
            {/* Admin-only inline edit. Hidden once the WO is locked
                (status=completed) so we don't tempt anyone into
                rewriting history on a closed job — the Reopen button
                replaces it for that case. Edit IS allowed on WOs that
                already sit on a draft/sent invoice; the form shows a
                warning so the admin knows the invoice line stays put
                and won't auto-update. */}
            {isAdmin && wo.status !== 'completed' && (
              <button
                type="button"
                onClick={() => setEditing(v => !v)}
                title={editing ? 'Cancel edit' : 'Edit work order'}
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border transition-colors ${
                  editing
                    ? 'bg-slate-900 text-white border-slate-900 hover:bg-slate-800'
                    : 'text-slate-500 border-slate-200 hover:bg-slate-50 hover:text-slate-700'
                }`}
                data-testid={`wo-edit-toggle-${wo.id}`}
              >
                <Pencil className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{editing ? 'Editing' : 'Edit'}</span>
              </button>
            )}
            {/* Admin-only "Reopen". Visible only when the job is marked
                completed — flips it back to in-progress so labour /
                parts / RAMS are editable again. If the auto-accumulator
                already raised a draft invoice we warn first: the line
                item stays on the customer's invoice and won't shift,
                so reopening is for fixing the WO record, not
                un-billing. */}
            {isAdmin && wo.status === 'completed' && (
              <button
                type="button"
                onClick={() => reopen()}
                disabled={reopenBusy}
                title="Reopen this work order"
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-60"
                data-testid={`wo-reopen-${wo.id}`}
              >
                <Undo2 className={`h-3.5 w-3.5 ${reopenBusy ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">{reopenBusy ? 'Reopening…' : 'Reopen'}</span>
              </button>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 min-w-0 max-w-full">
          <div className="min-w-0">
            <h2 className="text-xl font-display uppercase tracking-wide break-words">{wo.title}</h2>
            <p className="text-sm text-slate-600 mt-2 whitespace-pre-line break-words">{wo.description}</p>
            <LabourRollup wo={wo} isEngineer={isEngineer} />
            {!isEngineer && <BillingPanel wo={wo} onUpdate={onUpdate} />}
          </div>
          {editing && (
            <EditWorkOrderForm
              wo={wo}
              onClose={() => setEditing(false)}
              onSaved={() => { onUpdate?.(); }}
            />
          )}
          {/* Admin review banner — shows when an engineer marked the WO
              completed and the admin hasn't signed off yet. Without sign-off
              the Xero draft-invoice accumulator stays parked. */}
          {isAdmin && wo.needsAdminReview && (
            <AdminReviewBanner wo={wo} onUpdate={onUpdate} />
          )}
          {/* Engineer sees the admin's rejection notes when a WO has been
              sent back. Persists on the WO until the engineer closes it
              again (cleared server-side on re-completion). */}
          {isEngineer && wo.adminRejectNotes && (
            <div
              className="bg-rose-50 border border-rose-200 rounded-md p-3 text-sm"
              data-testid="wo-reject-banner"
            >
              <div className="flex items-start gap-2">
                <Undo2 className="h-4 w-4 text-rose-700 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-rose-900">
                    Admin sent this back — please fix and close again
                  </div>
                  <div className="text-rose-800 text-xs mt-0.5">
                    From <strong>{wo.adminRejectedBy}</strong>
                    {wo.adminRejectedAt && <> · {wo.adminRejectedAt.slice(0, 16).replace('T', ' ')}</>}
                  </div>
                  <div className="text-rose-900 text-sm mt-1.5 whitespace-pre-line">{wo.adminRejectNotes}</div>
                </div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="flex items-start gap-2 text-slate-700">
              <Wrench className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
              <AssetsDisplay wo={wo} />
            </div>
            <div className="flex items-center gap-2 text-slate-700"><Building2 className="h-4 w-4 text-slate-500" /> {wo.customer || 'Internal'}</div>
            {wo.siteCustomer && (wo.siteCustomerId || '') !== (wo.customerId || '') && (
              <div className="flex items-center gap-2 text-slate-700 sm:col-span-2" data-testid={`wo-site-customer-${wo.id}`}>
                <MapPin className="h-4 w-4 text-amber-500" />
                <span>
                  <span className="text-xs uppercase tracking-wider text-slate-500 mr-1">Site</span>
                  {wo.siteCustomer}
                </span>
              </div>
            )}
            <div className="flex items-center gap-2 text-slate-700"><UserIcon className="h-4 w-4 text-slate-500" /> {wo.assignee}</div>
            <div className="flex items-center gap-2 text-slate-700" data-testid={`wo-due-${wo.id}`}><Calendar className="h-4 w-4 text-slate-500" /> Due {wo.dueDate}</div>
            {/* Show planned start + finish dates whenever the admin set
                them. Hidden by default if both are blank to keep the
                summary tidy for ad-hoc work orders. */}
            {wo.startByDate && (
              <div className="flex items-center gap-2 text-slate-700" data-testid={`wo-start-by-${wo.id}`}>
                <Calendar className="h-4 w-4 text-emerald-500" />
                <span><span className="text-xs uppercase tracking-wider text-slate-500 mr-1">Start by</span>{wo.startByDate}</span>
              </div>
            )}
            {wo.finishByDate && (
              <div className="flex items-center gap-2 text-slate-700" data-testid={`wo-finish-by-${wo.id}`}>
                <Calendar className="h-4 w-4 text-amber-500" />
                <span><span className="text-xs uppercase tracking-wider text-slate-500 mr-1">Finish by</span>{wo.finishByDate}</span>
              </div>
            )}
            {/* Completion date pill. Visible whenever the WO is closed
                so the timeline is obvious at a glance. Clickable for
                admins so they can retro-adjust a paper job that was
                actually finished last Tuesday. Engineers see it
                read-only. */}
            {wo.status === 'completed' && (
              <CompletedAtPill wo={wo} canEdit={isAdmin} onUpdate={onUpdate} />
            )}
          </div>
          <div className="pt-4 border-t border-slate-200">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Attachments</div>
            {canAttach ? (
              <AttachmentUploader
                existing={wo.attachments || []}
                label="Attach photo or document"
                onUpload={async (file) => {
                  const att = await uploadWorkOrderAttachment(wo.id, file);
                  if (onUpdate) onUpdate();
                  return att;
                }}
              />
            ) : (
              (wo.attachments || []).length === 0
                ? <div className="text-sm text-slate-500">No attachments.</div>
                : null
            )}
          </div>
          <WorkOrderPartsPanel workOrder={wo} />
          <WorkOrderLaborPanel workOrder={wo} onChange={onUpdate} />
          <WorkOrderRamsPanel
            workOrderId={wo.id}
            latestRamsAt={wo.latestRamsAt}
            wo={wo}
            onAckChange={onUpdate}
          />

          {/* Sign-off block. Renders in three states:
                - not yet signed + canSignOff  → prompt with "Sign off" CTA
                - signed                       → read-only receipt with name, timestamp
                - not signed and no permission → nothing (hidden for read-only viewers)
              The signature ends up embedded on the WO PDF so recipients get a
              tamper-evident, self-contained hand-off document. */}
          {(woView.signOff?.signedAt || canSignOff) && (
            <SignOffPanel wo={woView} canSignOff={canSignOff} onOpenModal={() => setSignOffOpen(true)} />
          )}

          {/* Engineer "Close job" action — final step after timesheet,
              parts, and RAMS acknowledgement. Server-side blocks completion
              if the current RAMS version hasn't been acknowledged so this
              button is always safe to show; we just surface a toast with
              the backend's explanation if pressed without reading RAMS. */}
          {isEngineer && wo.status !== 'completed' && (
            <EngineerCloseButton wo={wo} onUpdate={onUpdate} />
          )}
        </div>
      </DialogContent>
    </Dialog>
    <PdfPreviewDialog
      url={pdf.url}
      filename={pdf.filename}
      onClose={pdf.close}
      title={`Work order ${wo.id}`}
    />
    <SignOffModal
      workOrder={wo}
      open={signOffOpen}
      onOpenChange={setSignOffOpen}
      onSigned={(updated) => {
        // Optimistic local overlay so the panel flips to SIGNED without
        // waiting on the parent list refetch, THEN kick off the parent
        // refresh so persisted state and the UI stay in sync long-term.
        if (updated?.signOff) setSignOffOverlay(updated.signOff);
        onUpdate && onUpdate(updated);
      }}
    />
    <PdfPreflightModal
      workOrderId={wo?.id}
      open={preflightOpen}
      onOpenChange={setPreflightOpen}
      onDownload={exportPdf}
    />
    </>
  );
};

/** Admin review banner. Lists any "needs rate" labour entries with an
 *  inline picker so the admin can fix them, then a single "Approve & invoice"
 *  CTA that flips `needsAdminReview` and triggers the Xero draft-accumulator. */
const AdminReviewBanner = ({ wo, onUpdate }) => {
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [labourRates, setLabourRates] = useState([]);
  const [resolving, setResolving] = useState({});  // index → bool
  // Per-row custom £/h input (admin types a rate directly). Keyed by
  // labour entry index so each entry gets its own tiny input.
  const [customRate, setCustomRate] = useState({});
  const pending = (wo.labor || [])
    .map((l, i) => ({ ...l, _i: i }))
    .filter(l => l.needsRateResolution);

  React.useEffect(() => {
    if (pending.length === 0) return;
    getInventoryParts()
      .then(all => setLabourRates(all.filter(p =>
        !p.isDraft && (p.category === 'Labor' || p.xeroIsTracked === false)
      )))
      .catch(() => {});
  }, [pending.length]);

  const apply = async (index, body, successMsg = 'Rate applied') => {
    setResolving(s => ({ ...s, [index]: true }));
    try {
      await resolvePendingLabourRate(wo.id, index, body);
      toast.success(successMsg);
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to apply rate');
    } finally {
      setResolving(s => ({ ...s, [index]: false }));
    }
  };

  const autoResolve = (index) => apply(index, { autoResolve: true }, 'Rate auto-resolved');
  const applyCustom = (index) => {
    const amt = parseFloat(customRate[index]);
    if (!Number.isFinite(amt) || amt < 0) return toast.error('Enter a £/hour');
    apply(index, { rateAmount: amt });
  };
  const applyLegacy = (index, partId) => {
    if (!partId) return;
    apply(index, { partId });
  };

  const approve = async () => {
    setApproving(true);
    try {
      await approveWorkOrder(wo.id);
      toast.success('Approved · draft invoice queued');
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed');
    } finally { setApproving(false); }
  };

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-3" data-testid="wo-admin-review-banner">
      <div className="flex items-start gap-2">
        <ShieldCheck className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
        <div className="flex-1 text-sm">
          <div className="font-semibold text-amber-900">Awaiting your review</div>
          <div className="text-amber-800 text-xs mt-0.5">
            Engineer has completed this job. Approve to clear and create a draft invoice in Xero.
          </div>
        </div>
      </div>
      {/* Engineer's closure summary — what they wrote before hitting close.
          This is purely for the admin's review; it does NOT flow into the
          Xero invoice description. */}
      {wo.closureSummary && (
        <div
          className="bg-white border border-amber-200 rounded-md p-2.5 text-xs"
          data-testid="wo-closure-summary-admin"
        >
          <div className="text-amber-900 font-medium mb-0.5">What the engineer did</div>
          <div className="text-slate-800 whitespace-pre-line">{wo.closureSummary}</div>
          {wo.closureSummaryBy && (
            <div className="text-slate-400 text-[11px] mt-1">
              — {wo.closureSummaryBy}
              {wo.closureSummaryAt && <> · {wo.closureSummaryAt.slice(0, 16).replace('T', ' ')}</>}
            </div>
          )}
        </div>
      )}
      {pending.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-medium text-amber-900">
            {pending.length} labour {pending.length === 1 ? 'entry needs' : 'entries need'} a rate before you can approve:
          </div>
          {pending.map(p => {
            const busy = !!resolving[p._i];
            return (
              <div key={p._i} className="bg-white border border-amber-200 rounded p-2 space-y-2">
                <div className="flex items-center gap-2">
                  <Clock className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                  <div className="flex-1 text-xs min-w-0">
                    <div className="font-medium text-slate-900 truncate">{p.hours}h · {p.description}</div>
                    <div className="text-slate-500 truncate">{p.addedBy} · band: {p.timeBand || 'unknown'}</div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => autoResolve(p._i)}
                    disabled={busy}
                    className="h-8 text-xs"
                    data-testid={`wo-autoresolve-${p._i}`}
                    title="Re-run rate-card matcher for this engineer + customer + time band"
                  >
                    <Sparkles className="h-3 w-3 mr-1" /> Auto-resolve
                  </Button>
                  <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded px-1.5">
                    <PoundSterling className="h-3 w-3 text-slate-500" />
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      value={customRate[p._i] ?? ''}
                      onChange={e => setCustomRate(s => ({ ...s, [p._i]: e.target.value }))}
                      placeholder="£/h"
                      className="h-7 w-20 text-xs border-0 bg-transparent focus-visible:ring-0 px-0"
                      data-testid={`wo-custom-rate-input-${p._i}`}
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs px-2"
                      onClick={() => applyCustom(p._i)}
                      disabled={busy}
                      data-testid={`wo-custom-rate-apply-${p._i}`}
                    >
                      Apply
                    </Button>
                  </div>
                  {labourRates.length > 0 && (
                    <Select onValueChange={(v) => applyLegacy(p._i, v)} disabled={busy}>
                      <SelectTrigger className="h-8 w-44 text-xs" data-testid={`wo-resolve-rate-${p._i}`}>
                        <SelectValue placeholder="…or pick a legacy rate" />
                      </SelectTrigger>
                      <SelectContent>
                        {labourRates.map(r => (
                          <SelectItem key={r.id} value={r.id}>
                            {r.name} — £{(r.sellPrice ?? r.unitCost ?? 0).toFixed(2)}/h
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          onClick={approve}
          disabled={approving || pending.length > 0 || rejecting}
          className="bg-amber-700 hover:bg-amber-800 text-white"
          data-testid="wo-approve-btn"
        >
          <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
          {pending.length > 0 ? 'Resolve all rates first' : (approving ? 'Approving...' : 'Approve & create draft invoice')}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setRejectOpen(true)}
          disabled={approving || rejecting}
          className="border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-900"
          data-testid="wo-reject-open-btn"
        >
          <Undo2 className="h-3.5 w-3.5 mr-1.5" />
          Reopen with notes
        </Button>
      </div>

      <RejectDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        wo={wo}
        onRejected={() => { setRejectOpen(false); onUpdate?.(); }}
        busySetter={setRejecting}
      />
    </div>
  );
};

/** Admin rejection dialog. Requires non-empty notes so the engineer has
 * something actionable to work from — server-side also enforces this. */
const RejectDialog = ({ open, onOpenChange, wo, onRejected, busySetter }) => {
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) setNotes(''); }, [open]);
  const submit = async () => {
    const trimmed = notes.trim();
    if (!trimmed) return toast.error('Tell the engineer what to fix');
    setBusy(true); busySetter?.(true);
    try {
      await rejectWorkOrder(wo.id, trimmed);
      toast.success('Sent back to engineer');
      onRejected?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reopen');
    } finally {
      setBusy(false); busySetter?.(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="wo-reject-dialog">
        <DialogHeader>
          <DialogTitle>Reopen work order</DialogTitle>
          <DialogDescription>
            The engineer will see these notes and can fix + re-close the job.
            A record is kept in the WO history for audit.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={5}
          placeholder="e.g. Missing photo of isolation · parts list incomplete · re-test and re-close"
          data-testid="wo-reject-notes-input"
          className="resize-none"
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="wo-reject-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !notes.trim()}
            className="bg-rose-700 hover:bg-rose-800 text-white"
            data-testid="wo-reject-confirm"
          >
            {busy ? 'Reopening…' : 'Reopen with notes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Engineer-side "Close job" flow. Opens a small dialog where the engineer
 * writes a one-line summary of what they did on-site before the WO can be
 * marked completed. Backend enforces both the RAMS-ack gate and the
 * non-empty summary so we can fail gracefully on either constraint. */
/** SignOffPanel — small card inside the WO detail dialog that either:
 *   - shows a "Sign off" CTA to admins / assigned engineers, or
 *   - shows the read-only "Signed off by X on Y" receipt once signed.
 *  The captured signature is embedded on the WO PDF automatically. */
const SignOffPanel = ({ wo, canSignOff, onOpenModal }) => {
  const signed = !!wo?.signOff?.signedAt;
  const s = wo?.signOff || {};
  const when = signed
    ? new Date(s.signedAt).toLocaleString(undefined, {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : '';
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="wo-signoff-panel">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <PenTool className="h-4 w-4 text-slate-500" />
          <h3 className="font-display uppercase tracking-wide text-sm text-slate-700">Sign-off</h3>
          {signed ? (
            <span className="text-[10px] uppercase tracking-widest bg-emerald-50 text-emerald-800 px-1.5 py-0.5 rounded" data-testid="wo-signoff-status-signed">
              Signed
            </span>
          ) : (
            <span className="text-[10px] uppercase tracking-widest bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded" data-testid="wo-signoff-status-unsigned">
              Not signed
            </span>
          )}
        </div>
        {canSignOff && (
          <Button
            variant="outline"
            size="sm"
            onClick={onOpenModal}
            className="h-7 text-xs"
            data-testid="wo-signoff-open-btn"
          >
            <PenTool className="h-3 w-3 mr-1" />
            {signed ? 'Re-sign' : 'Sign off'}
          </Button>
        )}
      </div>
      {signed ? (
        <div className="text-sm text-slate-700 space-y-0.5" data-testid="wo-signoff-receipt">
          <div>
            Signed by <span className="font-medium text-slate-900">{s.signedByName || s.signedBy}</span>
            {s.signedRole ? <span className="text-slate-500"> · {s.signedRole}</span> : null}
          </div>
          <div className="text-xs text-slate-500">on {when}</div>
          <div className="text-xs text-slate-400 mt-1">
            The signature is embedded on this work order&rsquo;s PDF.
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-600">
          Sign this work order to embed an e-signature on the PDF — recipients get a self-contained,
          audit-ready hand-off document.
        </p>
      )}
    </div>
  );
};



const EngineerCloseButton = ({ wo, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState('');
  // Completion date picker. Defaults to today — the common case is
  // "closed on-site right now" — but the engineer can back-date it
  // (e.g. finished a job Friday evening, only logging it Monday).
  // Backend clamps to ≤ 30 days in the past + no future dates for
  // engineers.
  const [completedAt, setCompletedAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [closedLocal, setClosedLocal] = useState(false);
  useEffect(() => {
    if (!open) {
      setSummary('');
      setCompletedAt(new Date().toISOString().slice(0, 10));
    }
  }, [open]);
  const submit = async () => {
    const trimmed = summary.trim();
    setBusy(true);
    try {
      await updateWorkOrder(wo.id, {
        status: 'completed',
        closureSummary: trimmed,
        completedAt,
      });
      setClosedLocal(true);
      toast.success('Job closed — awaiting admin review');
      setOpen(false);
      onUpdate?.();
    } catch (e) {
      // Surface the actual backend reason (RAMS not acked, completion
      // date out of range, etc.) instead of a bare "Could not close
      // job" toast. Falls back through detail → message → raw body →
      // error.message.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail
                     || e?.response?.data?.message
                     || (typeof e?.response?.data === 'string' ? e.response.data : null)
                     || e?.message
                     || 'Could not close job';
      const prefix = status ? `Could not close job (${status})` : 'Could not close job';
      toast.error(
        <div>
          <div className="font-medium">{prefix}</div>
          <div className="text-xs mt-1 break-words">{String(detail).slice(0, 500)}</div>
        </div>,
        { duration: 12000 },
      );
      console.error('Close job failed:', e?.response || e);
    } finally {
      setBusy(false);
    }
  };
  if (closedLocal || wo.status === 'completed') return null;
  const todayIso = new Date().toISOString().slice(0, 10);
  return (
    <div className="pt-4 border-t border-slate-200">
      <Button
        onClick={() => setOpen(true)}
        className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
        data-testid="wo-engineer-close-btn"
      >
        <CheckCircle2 className="h-4 w-4 mr-2" />
        Close job
      </Button>
      <div className="text-[11px] text-slate-500 mt-1.5 text-center">
        RAMS must be marked as read first. A summary is optional; admin will review and either approve or send back with notes.
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="wo-close-dialog">
          <DialogHeader>
            <DialogTitle>Close this job</DialogTitle>
            <DialogDescription>
              Pick the actual completion date and optionally add a short
              summary — admin reviews before invoicing.
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-xs text-slate-600">Completion date</label>
            <input
              type="date"
              value={completedAt}
              onChange={(e) => setCompletedAt(e.target.value)}
              max={todayIso}
              className="mt-1 w-full border border-slate-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              data-testid="wo-close-date-input"
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Defaults to today. Back-date to when you actually finished on-site.
            </div>
          </div>
          <Textarea
            value={summary}
            onChange={e => setSummary(e.target.value)}
            rows={4}
            maxLength={500}
            placeholder="Optional — e.g. Replaced faulty contactor on Pump 2, tested on load, commissioned."
            className="resize-none"
            data-testid="wo-close-summary-input"
          />
          <div className="text-[11px] text-slate-400 -mt-2">
            {summary.trim().length}/500 · optional
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} data-testid="wo-close-cancel">
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={busy}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="wo-close-confirm"
            >
              <CheckCircle2 className="h-4 w-4 mr-1.5" />
              {busy ? 'Closing…' : 'Close job'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/**
 * Convenience button: self-contained eye-icon → opens the dialog.
 * Stops click propagation so it works inside a clickable row without
 * firing the row's own onClick twice.
 */
export const WorkOrderDetailButton = ({ wo, onUpdate, canAttach = true }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        data-testid={`wo-open-${wo.id}`}
        aria-label="Open work order"
      >
        <Eye className="h-4 w-4" />
      </Button>
      <WorkOrderDetailDialog
        wo={wo}
        open={open}
        onOpenChange={setOpen}
        onUpdate={onUpdate}
        canAttach={canAttach}
      />
    </>
  );
};
